import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './Shop.module.css';
import GlobalHeader from '../GlobalHeader';

type TabType = 'getCoins' | 'boards' | 'designs' | 'materials' | 'colors';

const pieceTypes = [
  'bear', 'crane', 'frog', 'goat', 'monkey',
  'mushroom', 'octopus', 'penguin', 'rhino',
  'snake', 'tree', 'virus', 'wolf'
];

const colorOptions = [
  { name: 'Red', hex: '#FF3B3B' },
  { name: 'Blue', hex: '#3B9BFF' },
  { name: 'Green', hex: '#3BFF6E' },
  { name: 'Purple', hex: '#B73BFF' },
  { name: 'Orange', hex: '#FF9B3B' },
  { name: 'Pink', hex: '#FF3BA3' },
  { name: 'Yellow', hex: '#FFE63B' },
  { name: 'Cyan', hex: '#3BFFEB' },
];

// Item rarity system
type Rarity = 'common' | 'uncommon' | 'rare' | 'epic' | 'legendary';

interface ShopItem {
  id: string;
  name: string;
  type: 'board' | 'design' | 'color' | 'material';
  rarity: Rarity;
  price: number;
  imageUrl?: string;
  description?: string;
}

// Rarity configuration
const rarityConfig: Record<Rarity, { color: string; priceMultiplier: number; weight: number }> = {
  common: { color: '#9CA3AF', priceMultiplier: 1, weight: 50 },
  uncommon: { color: '#22C55E', priceMultiplier: 2, weight: 30 },
  rare: { color: '#3B82F6', priceMultiplier: 4, weight: 15 },
  epic: { color: '#A855F7', priceMultiplier: 8, weight: 4 },
  legendary: { color: '#F59E0B', priceMultiplier: 15, weight: 1 },
};

// All possible shop items
const allShopItems: ShopItem[] = [
  // Boards
  { id: 'board-grass', name: 'Grass Board', type: 'board', rarity: 'common', price: 100, imageUrl: '/images/Terrain images/optimized/grasslight.png' },
  { id: 'board-water', name: 'Water Board', type: 'board', rarity: 'common', price: 100, imageUrl: '/images/Terrain images/optimized/oceanlight.png' },
  { id: 'board-sand', name: 'Sand Board', type: 'board', rarity: 'uncommon', price: 200, imageUrl: '/images/Terrain images/optimized/sandlight.png' },
  { id: 'board-ice', name: 'Ice Board', type: 'board', rarity: 'rare', price: 400, imageUrl: '/images/Terrain images/optimized/icelight.png' },
  { id: 'board-neon', name: 'Neon Grid Board', type: 'board', rarity: 'epic', price: 800, description: 'Futuristic neon board' },

  // Designs (8-bit pieces)
  { id: 'design-8bit-bear', name: '8-Bit Bear', type: 'design', rarity: 'uncommon', price: 200, imageUrl: '/8bit/biglightbear.png' },
  { id: 'design-8bit-crane', name: '8-Bit Crane', type: 'design', rarity: 'uncommon', price: 200, imageUrl: '/8bit/bigdarkcrane.png' },
  { id: 'design-8bit-frog', name: '8-Bit Frog', type: 'design', rarity: 'uncommon', price: 200, imageUrl: '/8bit/biglightfrog.png' },
  { id: 'design-8bit-goat', name: '8-Bit Goat', type: 'design', rarity: 'uncommon', price: 200, imageUrl: '/8bit/bigdarkgoat.png' },
  { id: 'design-scribble-bear', name: 'Scribble Bear', type: 'design', rarity: 'rare', price: 400, imageUrl: '/scribble/BearPSD.png' },

  // Colors
  { id: 'color-red', name: 'Red Dye', type: 'color', rarity: 'common', price: 100, description: 'Apply red color to pieces' },
  { id: 'color-blue', name: 'Blue Dye', type: 'color', rarity: 'common', price: 100, description: 'Apply blue color to pieces' },
  { id: 'color-green', name: 'Green Dye', type: 'color', rarity: 'common', price: 100, description: 'Apply green color to pieces' },
  { id: 'color-purple', name: 'Purple Dye', type: 'color', rarity: 'uncommon', price: 200, description: 'Apply purple color to pieces' },
  { id: 'color-orange', name: 'Orange Dye', type: 'color', rarity: 'uncommon', price: 200, description: 'Apply orange color to pieces' },
  { id: 'color-pink', name: 'Pink Dye', type: 'color', rarity: 'rare', price: 400, description: 'Apply pink color to pieces' },
  { id: 'color-yellow', name: 'Yellow Dye', type: 'color', rarity: 'rare', price: 400, description: 'Apply yellow color to pieces' },
  { id: 'color-cyan', name: 'Cyan Dye', type: 'color', rarity: 'epic', price: 800, description: 'Apply cyan color to pieces' },

  // Materials
  { id: 'material-wood', name: 'Wood Material', type: 'material', rarity: 'uncommon', price: 200, description: 'Natural wooden texture' },
  { id: 'material-metallic', name: 'Metallic Material', type: 'material', rarity: 'rare', price: 400, description: 'Shiny metallic finish' },
  { id: 'material-glass', name: 'Glass Material', type: 'material', rarity: 'epic', price: 800, description: 'Translucent glass effect' },
  { id: 'material-crystal', name: 'Crystal Material', type: 'material', rarity: 'legendary', price: 1500, description: 'Rare prismatic crystal' },
  { id: 'material-obsidian', name: 'Obsidian Material', type: 'material', rarity: 'legendary', price: 1500, description: 'Deep black volcanic glass' },
];

// Seeded random number generator for consistent daily rotations
function seededRandom(seed: number): number {
  const x = Math.sin(seed) * 10000;
  return x - Math.floor(x);
}

// Get daily seed based on date
function getDailySeed(): number {
  const today = new Date();
  const year = today.getFullYear();
  const month = today.getMonth();
  const day = today.getDate();
  return year * 10000 + month * 100 + day;
}

// Select 5 items weighted by rarity
function getDailyShopItems(seed: number): ShopItem[] {
  const items = [...allShopItems];
  const selected: ShopItem[] = [];

  for (let i = 0; i < 5 && items.length > 0; i++) {
    // Calculate total weight
    const totalWeight = items.reduce((sum, item) => sum + rarityConfig[item.rarity].weight, 0);

    // Pick random value
    const randValue = seededRandom(seed + i) * totalWeight;

    // Select item based on weight
    let currentWeight = 0;
    let selectedIndex = 0;

    for (let j = 0; j < items.length; j++) {
      currentWeight += rarityConfig[items[j].rarity].weight;
      if (randValue <= currentWeight) {
        selectedIndex = j;
        break;
      }
    }

    selected.push(items[selectedIndex]);
    items.splice(selectedIndex, 1);
  }

  return selected;
}

function Shop() {
  const navigate = useNavigate();
  const [timeUntilRefresh, setTimeUntilRefresh] = useState<string>('');

  // Get daily shop items (memoized so it doesn't change during session)
  const dailyItems = useMemo(() => getDailyShopItems(getDailySeed()), []);

  // Calculate time until next refresh (midnight)
  useEffect(() => {
    const updateTimer = () => {
      const now = new Date();
      const tomorrow = new Date(now);
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(0, 0, 0, 0);

      const diff = tomorrow.getTime() - now.getTime();
      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      setTimeUntilRefresh(`${hours}h ${minutes}m ${seconds}s`);
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, []);

  const renderDailyShop = () => (
    <div className={styles.dailyShopContainer}>
      <div className={styles.shopHeader}>
        <h2 className={styles.dailyTitle}>Today's Offerings</h2>
        <div className={styles.refreshTimer}>
          <span className={styles.refreshLabel}>Shop refreshes in:</span>
          <span className={styles.refreshTime}>{timeUntilRefresh}</span>
        </div>
      </div>

      <p className={styles.shopDescription}>
        Five carefully selected items appear each day. Items are weighted by rarity -
        legendary items are extremely rare! Check back daily to see what's available.
      </p>

      <div className={styles.dailyItemsGrid}>
        {dailyItems.map((item, index) => (
          <div key={item.id} className={styles.dailyItemCard}>
            {/* Rarity badge */}
            <div
              className={styles.rarityBadge}
              style={{ backgroundColor: rarityConfig[item.rarity].color }}
            >
              {item.rarity.toUpperCase()}
            </div>

            {/* Item image or icon */}
            <div className={styles.itemImageContainer}>
              {item.imageUrl ? (
                <img
                  src={item.imageUrl}
                  alt={item.name}
                  className={styles.itemImage}
                />
              ) : (
                <div className={styles.itemPlaceholder}>
                  {item.type === 'board' && '🎯'}
                  {item.type === 'design' && '🎮'}
                  {item.type === 'color' && '🎨'}
                  {item.type === 'material' && '✨'}
                </div>
              )}
            </div>

            {/* Item info */}
            <div className={styles.itemInfo}>
              <h3 className={styles.itemName}>{item.name}</h3>
              <p className={styles.itemType}>{item.type.charAt(0).toUpperCase() + item.type.slice(1)}</p>
              {item.description && (
                <p className={styles.itemDescription}>{item.description}</p>
              )}
            </div>

            {/* Price and purchase */}
            <div className={styles.itemFooter}>
              <div className={styles.itemPrice}>🪙 {item.price}</div>
              <button className={styles.itemPurchaseButton}>Purchase</button>
            </div>
          </div>
        ))}
      </div>

      {/* Currency purchase section */}
      <div className={styles.currencySection}>
        <h3 className={styles.currencyTitle}>Need More Coins?</h3>
        <div className={styles.currencyOptions}>
          <button className={styles.currencyButton}>🪙 100 - $0.99</button>
          <button className={styles.currencyButton}>🪙 550 - $4.99</button>
          <button className={styles.currencyButton}>🪙 1,200 - $9.99</button>
        </div>
      </div>
    </div>
  );


  return (
    <div className={styles.container}>
      <GlobalHeader />

      <div className={styles.shopContainer}>
        <div className={styles.header}>
          <button
            className={styles.backToMain}
            onClick={() => navigate('/')}
          >
            ← Back to Main
          </button>
          <h1 className={styles.title}>Daily Shop</h1>
          <div className={styles.chipBalance}>
            🪙 1,000 Coins
          </div>
        </div>

        <div className={styles.content}>
          {renderDailyShop()}
        </div>
      </div>
    </div>
  );
}

export default Shop;
